import { getType, isRange } from '@/helpers/common'
import { fromEntries } from '@/helpers/javascript'
import api from '@/api'

// initial state
const state = {
	filterValues: {},
	entityFilterId: null,
	entityFilterValue: null,
	ruleFilterId: null,
	filterAux: false,
	projectionAux: false,
	exportData: true,
	exportDownloadUrl: null,
	rows: [],
	entries: 100,
	page: 0,
	totalEntries: null,
	txId: null,
	txRules: {}
}

// getters
const getters = {
	entities (state, getters, rootState) {
		return rootState.schema.entities
	},
	properties (state, getters, rootState) {
		return rootState.properties.properties
	},
	all (state, getters) {
		return getters.properties.all
	},
	filter (state, getters) {
		return getters.properties.Filter
	},
	projection (state, getters) {
		return getters.properties.Projection
	},
	baseFilters (state, getters) {
		return getters.filter.base.map(f => getters.all[f])
	},
	auxFilters (state, getters) {
		return getters.filter.aux.map(f => getters.all[f])
	},
	baseFilterValues (state, getters) {
		return getters.baseFilters.map(f => state.filterValues[f.name] || (isRange({ property: f, definitions: getters.entities }) ? [null, true, null, true] : [null, false]))
	},
	auxFilterValues (state, getters) {
		return getters.auxFilters.map(f => state.filterValues[f.name] || (isRange({ property: f, definitions: getters.entities }) ? [null, true, null, true] : [null, false]))
			.concat(state.entityFilterId ? [state.entityFilterValue] : [])
	},
	matchFilterQuery (state, getters) {
		return (filters, values) => {
			return filters.map((v, i) => ({ i, v })).filter(f => !isRange({ property: f.v, definitions: getters.entities })).map(f => [f.v.path].concat(values[f.i]))
				.filter(f => f[1] !== null || f[2] === true).map(f => [f[0], { match: f[1] }])
		}
	},
	rangeFilterQuery (state, getters) {
		return (filters, values) => {
			return filters.map((v, i) => ({ i, v })).filter(f => isRange({ property: f.v, definitions: getters.entities })).map(f => [f.v.path].concat(values[f.i]))
				.filter(f => f[1] || f[3]).map(f => [f[0], { from: f[1], to: f[3] }])
		}
	},
	baseMatchFilterQuery (state, getters) {
		return getters.matchFilterQuery(getters.baseFilters, getters.baseFilterValues)
	},
	baseRangeFilterQuery (state, getters) {
		return getters.rangeFilterQuery(getters.baseFilters, getters.baseFilterValues)
	},
	baseFilterQuery (state, getters) {
		return getters.baseMatchFilterQuery.concat(getters.baseRangeFilterQuery)
	},
	auxMatchFilterQuery (state, getters) {
		return getters.matchFilterQuery(getters.auxFilters, getters.auxFilterValues)
	},
	auxRangeFilterQuery (state, getters) {
		return getters.rangeFilterQuery(getters.auxFilters, getters.auxFilterValues)
	},
	auxFilterQuery (state, getters) {
		return getters.auxMatchFilterQuery.concat(getters.auxRangeFilterQuery)
	},
	entityFilterQuery (state, getters) {
		const entity = getters.entities[state.entityFilterId]
		const path = entity && entity.name
		return [[path].concat(getters.auxFilterValues[getters.auxFilterValues.length - 1] || [null])]
			.filter(f => f[0] && (f[1] !== null || f[2] === true)).map(f => [f[0], { match: f[1] }])
	},
	filterQuery (state, getters) {
		if (!state.filterAux) {
			return fromEntries(getters.baseFilterQuery)
		} else {
			return fromEntries(getters.baseFilterQuery.concat(getters.auxFilterQuery).concat(getters.entityFilterQuery))
		}
	},
	baseProjection (state, getters) {
		return getters.projection.base.map(f => getters.all[f])
	},
	auxProjection (state, getters) {
		return getters.projection.aux.map(f => getters.all[f])
	},
	projectionQuery (state, getters) {
		return getters.baseProjection.concat(getters.auxProjection).map(f => f.path)
	},
	projectionLabels (state, getters) {
		return getters.baseProjection.concat(getters.auxProjection).map(f => f.name)
	},
	limit (state) {
		return state.entries || -1
	},
	offset (state) {
		return state.entries * state.page
	},
	queryObject (state, getters) {
		return { filter: getters.filterQuery, projection: getters.projectionQuery, ruleId: state.ruleFilterId, limit: getters.limit, offset: getters.offset }
	},
	exportQueryObject (state, getters) {
		return { filter: getters.filterQuery, projection: getters.projectionQuery, labels: getters.projectionLabels, ruleId: state.ruleFilterId }
	},
	isNonEmpty (state, getters) {
		const query = getters.queryObject
		console.debug(query)
		return (Object.values(query.filter).length > 0 || query.ruleId) && query.projection.length > 0
	},
	isExportable (state, getters) {
		const query = getters.exportQueryObject
		return state.exportData && (Object.values(query.filter).length > 0 || query.ruleId) && query.projection.length > 0 && state.rows.length > 0
	}
}

function filterValue (type, value) {
	let castedValue
	if (value === '') {
		castedValue = null
	} else if (type === 'String') {
		castedValue = value
	} else if (type === 'Long') {
		castedValue = Number.parseInt(value)
	} else {
		castedValue = null
	}
	return [castedValue]
}

function rangeValue (type, value) {
	let fromCastedValue
	let toCastedValue
	if (!value[0]) {
		fromCastedValue = null
	} else if (['Integer', 'Long', 'Timestamp'].includes(type)) {
		fromCastedValue = Number.parseInt(value[0])
	} else if (type === 'Amount') {
		fromCastedValue = Number.parseFloat(value[0])
	} else {
		fromCastedValue = null
	}
	if (!value[1]) {
		toCastedValue = null
	} else if (['Integer', 'Long', 'Timestamp'].includes(type)) {
		toCastedValue = Number.parseInt(value[1])
	} else if (type === 'Amount') {
		toCastedValue = Number.parseFloat(value[1])
	} else {
		toCastedValue = null
	}
	return [fromCastedValue, fromCastedValue === null, toCastedValue, toCastedValue === null]
}

function filterValueNull (flag) {
	return [null, flag]
}

function rangeValueNull (current, from, to) {
	let target
	if (from !== null) {
		target = [null, from, current[2], current[3]]
	}
	if (to !== null) {
		target = [current[0], current[1], null, to]
	}
	return target
}

// actions
const actions = {
	setMissingFilterValues ({ getters, commit }) {
		commit('SET_MISSING_FILTER_VALUES', { filters: getters.baseFilters, entities: getters.entities })
		commit('SET_MISSING_FILTER_VALUES', { filters: getters.auxFilters, entities: getters.entities })
	},
	updateFilterValue ({ state, getters, commit }, { aux, index, value, isNull = null, from = null, to = null }) {
		let name
		let property

		if (aux && index === getters.filter.aux.length) {
			if (state.entityFilterId) {
				property = { definitionName: state.entityFilterId }
			}
		} else {
			name = aux ? getters.filter.aux[index] : getters.filter.base[index]
			property = getters.all[name]
		}
		if (property) {
			let target
			const type = getType({ property, definitions: getters.entities })
			if (isRange({ property, definitions: getters.entities })) {
				if (!from && from !== false && !to && to !== false) {
					target = rangeValue(type, value)
				} else {
					const current = name ? state.filterValues[name] || [] : state.entityFilterValue
					target = rangeValueNull(current, from, to)
				}
			} else {
				if (isNull === null) {
					target = filterValue(type, value)
				} else {
					target = filterValueNull(isNull)
				}
			}
			if (name) {
				commit('SET_PROPERTY_FILTER_VALUE', { name, target })
			} else {
				commit('SET_ENTITY_FILTER_VALUE', target)
			}
		}
	},
	async fetchData ({ commit, dispatch, getters }) {
		if (getters.isNonEmpty) {
			const query = getters.queryObject
			const response = await api.monitoring.page(query)
			const pageData = response.data
			const data = pageData.data;
			commit('SET_DATA', data)
			commit('SET_PAGE', pageData.page)
			commit('SET_TOTAL', pageData.total)
			dispatch('lists/setTxEntities', { rows: data, projection: getters.projection.base.concat(getters.projection.aux).map(p => getters.all[p].definitionName) }, { root: true })
		}
	},
	async exportData ({ commit, getters }) {
		if (getters.isExportable) {
			const query = getters.exportQueryObject
			commit('SET_EXPORT_DATA', false)
			try {
				const response = await api.monitoring.export(query)
				const data = response.data
				commit('SET_EXPORT_DOWNLOAD_URL', data)
			} finally {
				commit('SET_EXPORT_DATA', true)
			}
		}
	},
	async fetchTxRules ({ commit }, id) {
		const response = await api.monitoring.txRules(id)
		commit('SET_TX_RULES', { id, rules: response.data })
	}
}

// mutations
const mutations = {
	SET_MISSING_FILTER_VALUES (state, { filters, entities }) {
		filters.forEach(f => {
			if (!state.filterValues[f.name]) {
				let value
				if (isRange({ property: f, definitions: entities })) {
					value = [null, true, null, true]
				} else {
					value = [null, false]
				}
				state.filterValues[f.name] = value
			}
		})
	},
	SET_DATA (state, data) {
		state.rows = data
	},
	SET_PAGE (state, page) {
		state.page = page
	},
	SET_TOTAL (state, total) {
		state.totalEntries = total
	},
	TOGGLE_FILTER_AUX (state) {
		state.filterAux = !state.filterAux
	},
	TOGGLE_PROJECTION_AUX (state) {
		state.projectionAux = !state.projectionAux
	},
	SET_EXPORT_DATA (state, flag) {
		state.exportData = flag
	},
	SET_EXPORT_DOWNLOAD_URL (state, url) {
		state.exportDownloadUrl = url
	},
	SET_PROPERTY_FILTER_VALUE (state, { name, target }) {
		state.filterValues[name] = target
	},
	SET_ENTITY_FILTER_VALUE (state, value) {
		state.entityFilterValue = value
	},
	SET_ENTITY_FILTER_ID (state, id) {
		state.entityFilterId = id
		state.entityFilterValue = null
	},
	SET_RULE_FILTER_ID (state, id) {
		state.ruleFilterId = id
	},
	SET_TX_ID (state, id) {
		state.txId = id
	},
	SET_TX_RULES (state, { id, rules }) {
		state.txRules[id] = rules
	}
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}
